import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogTitle,
  CircularProgress,
} from "@material-ui/core";

import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import Switch from "@material-ui/core/Switch";
import { MuiChipsInput } from "mui-chips-input";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
    maxWidth: "500px",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  keywords: {
    width: "100%",
    "& input": {
      width: "100%!important",
    },
  },
}));

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .min(2, "Muito Curto(a)!")
    .max(15, "Muito Longo(a)!")
    .required("Obrigatório"),
  message: Yup.string()
    .min(8, "Muito Curto(a)!")
    .max(30000, "Muito Longo(a)!")
    .required("Obrigatório"),
});

const CategoryAIModal = ({
  open,
  onClose,
  categoryId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [submittingContent, setSubmittingContent] = useState(false);

  const initialState = {
    adminId: user.adminId,
    name: "",
    description: "",
    keywords: [],
    queues: [],
    updatedBy: "",
    status: true,
  };

  const [category, setCategory] = useState(initialState);

  // useEffect(() => {
  //   const fetchQueues = async () => {
  //     try {
  //       const { data } = await api.get("/queue/");
  //       setQueues(data.queues);
  //     } catch (err) {
  //       toastError(err);
  //     }
  //   };
  //   fetchQueues();
  // }, []);

  const handleClose = () => {
    onClose();
    setCategory(initialState);
  };

  const handleSaveCategory = async (values) => {
    try {
      if (categoryId) {
        await api.put(`/categories/${categoryId}`, values);
        handleClose();
      } else {
        const { data } = await api.post("/categories", values);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("categoryAIModal.success"));
      setSubmittingContent(false);
    } catch (err) {
      console.error({ err });
      err.response.data.error = i18n.t(err.response.data.error);
      toastError(err);
      setSubmittingContent(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchCategory = async () => {
      if (initialValues) {
        setCategory((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      // try {
      //   const { data } = await api.get(`/tags`, {
      //     signal: abortController.signal,
      //   });
      //   if (!isMounted) return;
      // } catch (err) {
      //   if (!isMounted || err.name == "AbortError") return;
      //   console.error({ err });
      // }

      if (!categoryId) return;

      try {
        const { data } = await api.get(`/categories/${categoryId}`, {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        const array = {
          adminId: data.adminId,
          name: data.name,
          description: data.description,
          status: data.status,
          keywords: data.keywords,
        };
        setCategory(array);
      } catch (err) {
        if (!isMounted || err.name == "AbortError") return;
        console.error({ err });
        toastError(err);
      }
    };

    fetchCategory();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [categoryId, open, initialValues]);

  const handleChangeChip = (keywords) => {
    const removeDuplicates = [...new Set(keywords)];
    if (removeDuplicates.length > 10) return;
    setCategory({ ...category, keywords: removeDuplicates });
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open == true ? true : false}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {categoryId
            ? `${i18n.t("categoryAIModal.title.edit")}`
            : `${i18n.t("categoryAIModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={category}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setSubmittingContent(true);
            setTimeout(() => {
              let array = {
                adminId: user.adminId,
                updatedBy: user.id,
                name: values.name,
                description: values.description,
                status: values.status,
                id: !categoryId ? null : categoryId,
                keywords: values.keywords,
              };
              handleSaveCategory(array);
            }, 400);
          }}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form style={{ padding: "10px" }}>
              <div className="form-row">
                <Field as={TextField} type="hidden" name="adminId" />
                <div className="form-group col-md-12 pl-3">
                  <label>
                    {i18n.t("categoryAIModal.form.name")}
                    <span className="text-danger ml-2">*</span>
                  </label>
                  <Field
                    style={{
                      borderColor: "#c4c4c4",
                      borderRadius: "3px",
                    }}
                    className="form-control"
                    placeholder={i18n.t("categoryAIModal.form.name")}
                    name="name"
                    autoFocus
                    required={true}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    maxLength={20}
                    onChange={(e) => {
                      setCategory({ ...category, name: e.target.value });
                    }}
                  />
                </div>

                <div className="form-group col-md-12 pl-3">
                  <label>{i18n.t("categoryAIModal.form.description")}</label>
                  <Field
                    as={TextField}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    name="description"
                    style={{ marginBottom: "15px" }}
                  />
                </div>

                <div className="form-group col-md-12 pl-3">
                  <label>{i18n.t("categoryAIModal.form.keywords")}</label>
                  <MuiChipsInput
                    className={classes.keywords}
                    size="small"
                    placeholder="Digite e pressione enter"
                    value={values?.keywords || []}
                    onChange={(e) => handleChangeChip(e)}
                  />
                </div>
               
                <div className="form-group col-md-12 pl-3">
                  <label>
                    {i18n.t("categoryAIModal.form.status")}
                    <span className="text-danger ml-2">*</span>
                  </label>
                  <Field
                    as={Switch}
                    color="primary"
                    name="status"
                    checked={values.status}
                    onChange={(e) => {
                      setCategory({ ...category, status: !category.status });
                    }}
                  />
                </div>
              </div>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={submittingContent}
                  variant="outlined"
                >
                  {i18n.t("categoryAIModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={submittingContent}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {categoryId
                    ? `${i18n.t("categoryAIModal.buttons.okEdit")}`
                    : `${i18n.t("categoryAIModal.buttons.okAdd")}`}
                  {submittingContent && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CategoryAIModal;
